import { ChevronDownIcon, ExternalLinkIcon, MessageSquareTextIcon } from "lucide-react";
import type { CmmdTaskConversation, CmmdTaskConversationSummary } from "@cmmd-forge/contracts";

import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { RESPONSIVE_TEXT_BUTTON_CLASS } from "~/lib/touchTarget";
import { ProjectTaskConversationTranscript } from "./ProjectTaskConversationTranscript";
import { formatTaskDateTime } from "./taskDateTime";

export interface ProjectTaskConversationHistoryProps {
  readonly conversations: readonly CmmdTaskConversationSummary[];
  readonly nextCursor: string | null;
  readonly selectedConversation: CmmdTaskConversation | null;
  readonly expandedConversationId: string | null;
  readonly loaded: boolean;
  readonly loading: boolean;
  readonly loadingMore: boolean;
  readonly loadingConversationId: string | null;
  readonly loadingMoreEvents: boolean;
  readonly error: string | null;
  readonly detailError: string | null;
  readonly onRetry: () => void;
  readonly onLoadMore: () => void;
  readonly onLoadConversation: (conversationId: string) => void;
  readonly onLoadMoreEvents: () => void;
  readonly onClearConversation: () => void;
  readonly onOpenConversation: () => void;
}

export function ProjectTaskConversationHistory(props: ProjectTaskConversationHistoryProps) {
  if (props.loading && props.conversations.length === 0) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading conversation history…
      </p>
    );
  }
  if (!props.loaded && props.error === null) return null;
  if (props.conversations.length === 0 && props.error === null) return null;

  return (
    <section aria-label="Task conversation history" className="space-y-2">
      {props.error !== null ? (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-md border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive"
        >
          <span>{props.error}</span>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className={RESPONSIVE_TEXT_BUTTON_CLASS}
            onClick={props.onRetry}
          >
            Retry
          </Button>
        </div>
      ) : null}
      {props.conversations.length === 0 ? null : <ConversationHistoryContent {...props} />}
    </section>
  );
}

function ConversationHistoryContent(props: ProjectTaskConversationHistoryProps) {
  const primary = props.conversations.filter((conversation) => conversation.linkKind === "primary");
  const related = props.conversations.filter((conversation) => conversation.linkKind === "related");
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 id="task-conversation-history-heading" className="text-sm font-semibold">
            Work conversations
          </h3>
          <p className="text-xs text-muted-foreground">
            The primary conversation and any related work context, stored in CMMD.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={RESPONSIVE_TEXT_BUTTON_CLASS}
          onClick={props.onOpenConversation}
        >
          <ExternalLinkIcon aria-hidden />
          Open work session
        </Button>
      </div>
      <ConversationGroup {...props} heading="Primary conversation" conversations={primary} />
      {related.length > 0 ? (
        <ConversationGroup {...props} heading="Additional conversations" conversations={related} />
      ) : null}
      {props.nextCursor !== null ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className={RESPONSIVE_TEXT_BUTTON_CLASS}
          disabled={props.loadingMore}
          onClick={props.onLoadMore}
        >
          {props.loadingMore ? "Loading…" : "Load more conversations"}
        </Button>
      ) : null}
    </>
  );
}

function ConversationGroup(
  props: Pick<
    ProjectTaskConversationHistoryProps,
    | "selectedConversation"
    | "expandedConversationId"
    | "loadingConversationId"
    | "loadingMoreEvents"
    | "detailError"
    | "onClearConversation"
    | "onLoadConversation"
    | "onLoadMoreEvents"
  > & {
    readonly heading: string;
    readonly conversations: readonly CmmdTaskConversationSummary[];
  },
) {
  return (
    <section aria-label={props.heading} className="space-y-1">
      <h4 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {props.heading}
      </h4>
      <ol className="divide-y divide-border border-y border-border">
        {props.conversations.map((conversation) => (
          <ConversationRow
            key={conversation.conversationId}
            conversation={conversation}
            {...props}
          />
        ))}
      </ol>
    </section>
  );
}

function ConversationRow(
  props: Pick<
    ProjectTaskConversationHistoryProps,
    | "selectedConversation"
    | "expandedConversationId"
    | "loadingConversationId"
    | "loadingMoreEvents"
    | "detailError"
    | "onClearConversation"
    | "onLoadConversation"
    | "onLoadMoreEvents"
  > & {
    readonly conversation: CmmdTaskConversationSummary;
  },
) {
  const { conversation } = props;
  const selected = props.selectedConversation?.conversationId === conversation.conversationId;
  const expanded = props.expandedConversationId === conversation.conversationId;
  const loading = props.loadingConversationId === conversation.conversationId;
  return (
    <li>
      <button
        type="button"
        aria-expanded={expanded}
        className="flex min-h-touch w-full items-center gap-3 px-2 py-2 text-left hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring md:min-h-11"
        onClick={() => {
          if (expanded) props.onClearConversation();
          else props.onLoadConversation(conversation.conversationId);
        }}
      >
        <MessageSquareTextIcon className="size-4 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1">
          <span className="block truncate text-sm font-medium text-foreground">
            {conversation.title || "Untitled conversation"}
          </span>
          <span className="mt-0.5 block truncate text-xs text-muted-foreground">
            {conversation.status} ·{" "}
            {formatTaskDateTime(conversation.lastActivityAt ?? conversation.attachedAt)}
          </span>
        </span>
        <span className="text-xs text-muted-foreground">{loading ? "Loading…" : "Transcript"}</span>
        <ChevronDownIcon
          className={cn("size-4 shrink-0 transition-transform", expanded ? "rotate-180" : null)}
          aria-hidden
        />
      </button>
      {selected ? (
        <ProjectTaskConversationTranscript
          conversation={props.selectedConversation}
          loadingMore={props.loadingMoreEvents}
          error={props.detailError}
          onLoadMore={props.onLoadMoreEvents}
        />
      ) : expanded && props.detailError !== null ? (
        <div className="border-t border-border bg-muted/30 px-3 py-3">
          <p role="alert" className="text-sm text-destructive">
            {props.detailError}
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className={`mt-3 ${RESPONSIVE_TEXT_BUTTON_CLASS}`}
            onClick={() => props.onLoadConversation(conversation.conversationId)}
          >
            Retry transcript
          </Button>
        </div>
      ) : null}
    </li>
  );
}
